// Using Web Crypto API for secure encryption/decryption
const ALGORITHM = process.env.REACT_APP_ENCRYPTION_ALGORITHM || "AES-GCM";
const KEY_LENGTH = Number(process.env.REACT_APP_ENCRYPTION_KEY_LENGTH) || 256;
const SALT_LENGTH = Number(process.env.REACT_APP_ENCRYPTION_SALT_LENGTH) || 16;
const IV_LENGTH = Number(process.env.REACT_APP_ENCRYPTION_IV_LENGTH) || 12;
const KEY_FOR_ENCRYPTION = process.env.REACT_APP_API_ENCRYPTION_KEY || "";

// Convert base64 to Uint8Array
const base64ToBuffer = (base64: string): Uint8Array => {
	const binaryString = window.atob(base64);
	const bytes = new Uint8Array(binaryString.length);
	for (let i = 0; i < binaryString.length; i++) {
		bytes[i] = binaryString.charCodeAt(i);
	}
	return bytes;
};

// Convert Uint8Array to base64
const bufferToBase64 = (buffer: Uint8Array): string => {
	const binaryString = Array.from(buffer)
		.map((byte) => String.fromCharCode(byte))
		.join("");
	return window.btoa(binaryString);
};

// Derive key from password using PBKDF2
async function deriveKey(
	password: string,
	salt: Uint8Array,
): Promise<CryptoKey> {
	const encoder = new TextEncoder();
	const passwordBuffer = encoder.encode(password);

	const importedKey = await crypto.subtle.importKey(
		"raw",
		passwordBuffer,
		"PBKDF2",
		false,
		["deriveKey"],
	);

	return crypto.subtle.deriveKey(
		{
			name: "PBKDF2",
			salt,
			iterations: 100000,
			hash: "SHA-256",
		},
		importedKey,
		{ name: ALGORITHM, length: KEY_LENGTH },
		false,
		["encrypt", "decrypt"],
	);
}

export async function encrypt(data: any): Promise<string> {
	const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
	const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
	const key = await deriveKey(KEY_FOR_ENCRYPTION, salt);

	const encoder = new TextEncoder();
	const dataBuffer = encoder.encode(JSON.stringify(data));

	const encrypted = await crypto.subtle.encrypt(
		{
			name: ALGORITHM,
			iv,
		},
		key,
		dataBuffer,
	);

	// Combine salt, IV, and encrypted data
	const result = new Uint8Array(salt.length + iv.length + encrypted.byteLength);
	result.set(salt, 0);
	result.set(iv, salt.length);
	result.set(new Uint8Array(encrypted), salt.length + iv.length);

	return bufferToBase64(result);
}

export async function decrypt(encryptedData: any): Promise<any> {
	try {
		const encrypted = base64ToBuffer(encryptedData);

		// Extract salt, IV, and data
		const salt = encrypted.slice(0, SALT_LENGTH);
		const iv = encrypted.slice(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
		const data = encrypted.slice(SALT_LENGTH + IV_LENGTH);

		const key = await deriveKey(KEY_FOR_ENCRYPTION, salt);

		const decrypted = await crypto.subtle.decrypt(
			{
				name: ALGORITHM,
				iv,
			},
			key,
			data,
		);

		const decoder = new TextDecoder();
		return JSON.parse(decoder.decode(decrypted));
	} catch (error) {
		console.error("Decryption failed:", error);
		return null;
	}
}
